import React from "react";
import { FaCheck, FaMagic, FaEye, FaInfoCircle } from "../../utils/icons";
import type { ProcessingStage } from "./types";

/**
 * Processing state (LU-A10 + LU-A8): a 3-node pipeline reassurance view.
 *
 * "Canonicalizing" (an internal term) was collapsed into "Analyzing" per Andrea —
 * canonicalize is just fast image-prep, so the user-facing chain is:
 *   Uploaded → Analyzing → Ready
 * Presentational; the page drives `stage` from its poll loop.
 *
 * `showPipeline=false` drops the node rail + heading and renders only the
 * reassurance note — used in the new-upload flow, where `UploadPipelineBox`
 * already shows the pipeline on top (one pipeline, not two). The full rail is
 * kept for the standalone case of viewing a still-processing past upload.
 */
interface ProcessingPipelineProps {
  stage: ProcessingStage;
  /** Show the 3-node rail + heading (default). False = reassurance note only. */
  showPipeline?: boolean;
}

const NODES: {
  key: ProcessingStage;
  label: string;
  Icon: React.ComponentType<{ className?: string }>;
}[] = [
  { key: "uploaded", label: "Uploaded", Icon: FaCheck },
  { key: "analyzing", label: "Analyzing", Icon: FaMagic },
  { key: "ready", label: "Ready", Icon: FaEye },
];

export const ProcessingPipeline: React.FC<ProcessingPipelineProps> = ({
  stage,
  showPipeline = true,
}) => {
  const activeIndex = NODES.findIndex((n) => n.key === stage);
  const fillPct = activeIndex <= 0 ? 0 : (activeIndex / (NODES.length - 1)) * 100;

  return (
    <div data-testid="upload-processing">
      {showPipeline && (
        <>
          <h3 className="text-[15px] font-medium text-fg">Processing your study</h3>
          <p className="mt-1.5 max-w-md text-[13px] leading-relaxed text-fg-muted">
            This usually takes a few minutes — it updates live, and appears in your list on the
            left.
          </p>

          <div className="relative mt-7 flex justify-between px-1.5">
            {/* base + fill rails */}
            <div className="absolute left-[15px] right-[15px] top-[15px] h-px bg-border" />
            <div
              className="absolute left-[15px] top-[15px] h-px bg-signal transition-[width] duration-500 ease-out"
              style={{ width: `calc((100% - 30px) * ${fillPct / 100})` }}
            />
            {NODES.map((node, i) => {
              const done = i < activeIndex;
              const active = i === activeIndex;
              const Icon = node.Icon;
              return (
                <div
                  key={node.key}
                  className="relative z-1 flex w-28 flex-col items-center gap-2.5"
                >
                  <span
                    className={[
                      "grid h-[30px] w-[30px] place-items-center rounded-[2px] border transition-colors",
                      active
                        ? "border-signal bg-signal-tint text-fg"
                        : done
                          ? "border-signal-stroke-soft bg-signal-ambient text-signal-text-tint"
                          : "border-border-strong bg-bg text-fg-dim",
                    ].join(" ")}
                  >
                    <Icon className="h-3.5 w-3.5" />
                  </span>
                  <span
                    className={[
                      "text-center font-mono text-[9px] uppercase tracking-widest",
                      active ? "text-fg" : done ? "text-fg-muted" : "text-fg/25",
                    ].join(" ")}
                  >
                    {node.label}
                  </span>
                </div>
              );
            })}
          </div>
        </>
      )}

      <div
        className={[
          "flex items-center gap-2.5 rounded-[2px] border border-border bg-surface px-3.5 py-3 text-xs text-fg-muted",
          showPipeline ? "mt-7" : "",
        ].join(" ")}
      >
        <FaInfoCircle className="h-3.5 w-3.5 flex-none text-fg-dim" />
        You can start another upload or leave — we&apos;ll keep processing in the background.
      </div>
    </div>
  );
};
